import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  Progress,
  HStack,
  Box,
  Spinner,
} from '@chakra-ui/react'
import { CheckCircle, Circle, XCircle } from 'lucide-react'

export type ProvisioningStep = {
  key: string
  label: string
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  message?: string
}

export type ProvisioningPhase = 'saving_client' | 'provisioning_database' | 'done' | 'failed'

interface ClientProvisioningModalProps {
  isOpen: boolean
  phase: ProvisioningPhase
  steps: ProvisioningStep[]
  databaseName?: string | null
  error?: string | null
  onRetry?: () => void
  onClose?: () => void
}

const phaseLabels: Record<ProvisioningPhase, string> = {
  saving_client: 'Saving client record',
  provisioning_database: 'Setting up client database',
  done: 'Client setup complete',
  failed: 'Setup failed',
}

function StepIcon({ status }: { status: ProvisioningStep['status'] }) {
  if (status === 'completed') {
    return <CheckCircle size={16} color="var(--chakra-colors-green-500)" />
  }
  if (status === 'failed') {
    return <XCircle size={16} color="var(--chakra-colors-red-500)" />
  }
  if (status === 'in_progress') {
    return <Spinner size="sm" color="blue.500" />
  }
  return <Circle size={16} color="var(--chakra-colors-gray-400)" />
}

export function ClientProvisioningModal({
  isOpen,
  phase,
  steps,
  databaseName,
  error,
  onRetry,
  onClose,
}: ClientProvisioningModalProps) {
  const completedCount = steps.filter((s) => s.status === 'completed').length
  const progressValue = steps.length > 0 ? (completedCount / steps.length) * 100 : phase === 'saving_client' ? 10 : 0
  const isDone = phase === 'done'
  const isFailed = phase === 'failed'

  return (
    <Modal isOpen={isOpen} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{phaseLabels[phase]}</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {databaseName ? (
              <Text fontSize="sm" color="gray.600">
                Database: <strong>{databaseName}</strong>
              </Text>
            ) : null}

            <Progress value={progressValue} size="sm" colorScheme={isFailed ? 'red' : isDone ? 'green' : 'blue'} borderRadius="md" />

            {phase === 'saving_client' ? (
              <HStack spacing={2}>
                <Spinner size="sm" color="blue.500" />
                <Text fontSize="sm">Creating client record...</Text>
              </HStack>
            ) : null}

            {steps.length > 0 ? (
              <VStack align="stretch" spacing={2}>
                {steps.map((step) => (
                  <Box key={step.key}>
                    <HStack spacing={2}>
                      <StepIcon status={step.status} />
                      <Text fontSize="sm" fontWeight={step.status === 'in_progress' ? 'semibold' : 'normal'}>
                        {step.label}
                      </Text>
                    </HStack>
                    {step.message ? (
                      <Text fontSize="xs" color="red.500" ml={6} mt={1}>
                        {step.message}
                      </Text>
                    ) : null}
                  </Box>
                ))}
              </VStack>
            ) : null}

            {error ? (
              <Text fontSize="sm" color="red.500">
                {error}
              </Text>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          {isFailed && onRetry ? (
            <Button colorScheme="blue" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
          {(isDone || isFailed) && onClose ? (
            <Button variant={isFailed ? 'ghost' : 'solid'} colorScheme={isDone ? 'green' : undefined} onClick={onClose} ml={isFailed && onRetry ? 2 : 0}>
              {isDone ? 'Done' : 'Close'}
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
